<template>
  <div class="timeline-tab">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Завантаження історії...</p>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!timeLineTicket || timeLineTicket.length === 0"
      class="empty-state"
    >
      <div class="empty-icon">
        <i class="mdi mdi-timeline-clock-outline"></i>
      </div>
      <h3>Історія порожня</h3>
      <p>Записи про дії з тікетом з'являться тут</p>
    </div>

    <!-- Timeline content -->
    <div v-else class="timeline-container">
      <div class="timeline-line"></div>

      <div
        v-for="(item, index) in timeLineTicket"
        :key="index"
        class="timeline-event"
        :class="getEventTypeClass(item.type)"
      >
        <div class="event-marker">
          <div class="marker-dot">
            <i :class="getEventIcon(item.type)"></i>
          </div>
        </div>

        <div class="event-card">
          <div class="event-header">
            <div
              class="event-type-badge"
              :class="getEventBadgeClass(item.type)"
            >
              {{ mutateType(item.type) }}
            </div>
            <div class="event-timestamp">
              <i class="mdi mdi-calendar-blank"></i>
              {{ item.date }}
              <span class="time-separator">•</span>
              <i class="mdi mdi-clock-outline"></i>
              {{ item.time }}
            </div>
          </div>

          <div class="event-body">
            <div class="event-user">
              <div class="user-avatar">
                {{ getInitials(item.workerName) }}
              </div>
              <div class="user-info">
                <div class="user-name">{{ item.workerName }}</div>
              </div>
            </div>

            <div class="event-content">
              <!-- Зміна середовища проблеми -->
              <div
                v-if="
                  item.historyData && item.historyData.oldProblemEnvironment
                "
                class="change-info"
              >
                <div class="change-label">Середовище проблеми:</div>
                <div class="change-value">
                  <span class="old-value">{{
                    mutateProblemEnv(item.historyData.oldProblemEnvironment)
                  }}</span>
                  <i class="mdi mdi-arrow-right-thin"></i>
                  <span class="new-value">{{
                    mutateProblemEnv(item.historyData.newProblemEnvironment)
                  }}</span>
                </div>
              </div>

              <!-- Зміна пріоритету -->
              <div
                v-if="item.historyData && item.historyData.oldPriority"
                class="change-info"
              >
                <div class="change-label">Пріоритет:</div>
                <div class="change-value">
                  <span class="old-value">{{
                    mutatepriority(item.historyData.oldPriority).name
                  }}</span>
                  <i class="mdi mdi-arrow-right-thin"></i>
                  <span class="new-value">{{
                    mutatepriority(Number(item.historyData.newPriority)).name
                  }}</span>
                </div>
              </div>

              <!-- Коментар -->
              <div v-if="item.comment" class="event-comment">
                <i class="mdi mdi-message-text-outline"></i>
                <span>{{ item.comment }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tickets } from "@/API.js";
import {
  mutateTypeTimelineTicket,
  mutateProblemEnv,
  mutatePriorityTicket,
} from "@/usabilityScripts/globalMutate.js";

let apiServe = new Tickets();

export default {
  name: "TimelineTab",
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      timeLineTicket: [],
      isInitialized: false,
    };
  },
  mounted() {
    this.initializeTimeline();
  },
  watch: {
    ticket: {
      handler(newTicket) {
        if (newTicket && newTicket.ticketId && this.isInitialized) {
          this.loadTimeline();
        }
      },
    },
  },
  methods: {
    initializeTimeline() {
      // Використовуємо Intersection Observer для відстеження видимості
      if (!this.isInitialized && this.$el) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isInitialized) {
              this.isInitialized = true;
              this.loadTimeline();
              observer.disconnect();
            }
          });
        });
        observer.observe(this.$el);
      }
    },

    loadTimeline() {
      if (!this.ticket || !this.ticket.ticketId) {
        return;
      }

      this.loading = true;
      apiServe
        .getTicketHistory(this.ticket.ticketId)
        .then((result) => {
          if (result.status === "done") {
            this.timeLineTicket = result.data.items;
          } else {
            this.$toast.error(this.$t("err"));
          }
        })
        .catch((error) => {
          console.error("Error loading timeline:", error);
          this.$toast.error(this.$t("err"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getInitials(string) {
      if (!string) return "";

      var names = string.split(" "),
        initials = names[0].substring(0, 1).toUpperCase();

      if (names.length > 1) {
        initials += names[names.length - 1].substring(0, 1).toUpperCase();
      }
      return initials;
    },
    mutateType(e) {
      return mutateTypeTimelineTicket(e);
    },
    mutatepriority(e) {
      return mutatePriorityTicket(e);
    },
    mutateProblemEnv(e) {
      return mutateProblemEnv(e);
    },
    getEventIcon(type) {
      const icons = {
        create_ticket: "mdi mdi-plus-circle",
        in_job_ticket: "mdi mdi-briefcase-check",
        decided_ticket: "mdi mdi-check-circle",
        close_ticket: "mdi mdi-lock",
        reopen_ticket: "mdi mdi-lock-open",
        joining_ticket: "mdi mdi-account-plus",
        exit_ticket: "mdi mdi-account-minus",
        changeProblem: "mdi mdi-swap-horizontal",
        changePriority: "mdi mdi-flag",
      };
      return icons[type] || "mdi mdi-circle-medium";
    },
    getEventTypeClass(type) {
      return `event-type-${type}`;
    },
    getEventBadgeClass(type) {
      const badges = {
        create_ticket: "badge-success",
        in_job_ticket: "badge-info",
        decided_ticket: "badge-primary",
        close_ticket: "badge-dark",
        reopen_ticket: "badge-warning",
        joining_ticket: "badge-info",
        exit_ticket: "badge-secondary",
        changeProblem: "badge-purple",
        changePriority: "badge-orange",
      };
      return badges[type] || "badge-default";
    },
  },
};
</script>

<style scoped>
.timeline-tab {
  padding: 24px;
  background: #ffffff;
  overflow-y: auto;
  height: 100vh;
  /* height: 100%; */
  /* box-sizing: border-box; */
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid #f1f3f5;
  border-top-color: #495057;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #868e96;
  font-size: 13px;
  font-weight: 400;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 36px;
  color: #adb5bd;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 500;
  color: #495057;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
  color: #868e96;
}

/* Timeline Container */
.timeline-container {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  margin-bottom: 250px;
}

.timeline-line {
  position: absolute;
  left: 16px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

/* Timeline Event */
.timeline-event {
  position: relative;
  margin-bottom: 24px;
  padding-left: 48px;
}

/* Event Marker */
.event-marker {
  position: absolute;
  left: 0;
  top: 12px;
}

.marker-dot {
  width: 32px;
  height: 32px;
  background: #ffffff;
  border: 2px solid #dee2e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.marker-dot i {
  font-size: 16px;
  color: #868e96;
}

/* Event Type Colors */
.event-type-create_ticket .marker-dot {
  border-color: #51cf66;
  background: #ffffff;
}

.event-type-create_ticket .marker-dot i {
  color: #51cf66;
}

.event-type-in_job_ticket .marker-dot {
  border-color: #339af0;
  background: #ffffff;
}

.event-type-in_job_ticket .marker-dot i {
  color: #339af0;
}

.event-type-decided_ticket .marker-dot {
  border-color: #845ef7;
  background: #ffffff;
}

.event-type-decided_ticket .marker-dot i {
  color: #845ef7;
}

.event-type-close_ticket .marker-dot {
  border-color: #495057;
  background: #ffffff;
}

.event-type-close_ticket .marker-dot i {
  color: #495057;
}

.event-type-reopen_ticket .marker-dot {
  border-color: #fcc419;
  background: #ffffff;
}

.event-type-reopen_ticket .marker-dot i {
  color: #fcc419;
}

.event-type-joining_ticket .marker-dot {
  border-color: #20c997;
  background: #ffffff;
}

.event-type-joining_ticket .marker-dot i {
  color: #20c997;
}

.event-type-exit_ticket .marker-dot {
  border-color: #adb5bd;
  background: #ffffff;
}

.event-type-exit_ticket .marker-dot i {
  color: #adb5bd;
}

.event-type-changeProblem .marker-dot {
  border-color: #9775fa;
  background: #ffffff;
}

.event-type-changeProblem .marker-dot i {
  color: #9775fa;
}

.event-type-changePriority .marker-dot {
  border-color: #ff6b6b;
  background: #ffffff;
}

.event-type-changePriority .marker-dot i {
  color: #ff6b6b;
}

/* Event Card */
.event-card {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

/* Event Header */
.event-header {
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.event-type-badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: #d3f9d8;
  color: #2f9e44;
}

.badge-info {
  background: #d0ebff;
  color: #1971c2;
}

.badge-primary {
  background: #e5dbff;
  color: #7048e8;
}

.badge-dark {
  background: #e9ecef;
  color: #343a40;
}

.badge-warning {
  background: #fff3bf;
  color: #e67700;
}

.badge-secondary {
  background: #e9ecef;
  color: #495057;
}

.badge-purple {
  background: #f3f0ff;
  color: #7950f2;
}

.badge-orange {
  background: #ffe3e3;
  color: #c92a2a;
}

.event-timestamp {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #868e96;
}

.event-timestamp i {
  font-size: 14px;
}

.time-separator {
  margin: 0 4px;
  color: #ced4da;
}

/* Event Body */
.event-body {
  padding: 16px;
}

.event-user {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #495057;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 13px;
  flex-shrink: 0;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #343a40;
}

/* Event Content */
.event-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.change-info {
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 2px solid #adb5bd;
}

.change-label {
  font-size: 11px;
  font-weight: 500;
  color: #868e96;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.change-value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  flex-wrap: wrap;
}

.old-value {
  padding: 2px 8px;
  background: #fff5f5;
  color: #c92a2a;
  border-radius: 3px;
  text-decoration: line-through;
}

.new-value {
  padding: 2px 8px;
  background: #d3f9d8;
  color: #2f9e44;
  border-radius: 3px;
  font-weight: 500;
}

.change-value i {
  color: #adb5bd;
  font-size: 16px;
}

.event-comment {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 2px solid #adb5bd;
}

.event-comment i {
  font-size: 16px;
  color: #868e96;
  flex-shrink: 0;
  margin-top: 1px;
}

.event-comment span {
  flex: 1;
  font-size: 13px;
  color: #495057;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .timeline-container {
    padding: 16px;
  }

  .timeline-line {
    left: 12px;
  }

  .timeline-event {
    padding-left: 40px;
  }

  .marker-dot {
    width: 28px;
    height: 28px;
  }

  .marker-dot i {
    font-size: 14px;
  }

  .event-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 12px;
  }

  .event-body {
    padding: 12px;
  }

  .event-timestamp {
    font-size: 11px;
  }

  .change-value {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .timeline-event {
    padding-left: 36px;
  }

  .timeline-line {
    left: 10px;
  }

  .marker-dot {
    width: 24px;
    height: 24px;
  }

  .marker-dot i {
    font-size: 12px;
  }

  .event-type-badge {
    font-size: 10px;
    padding: 3px 8px;
  }
}
</style>
